import {
  remindersFromActivity,
  remindersToPayload,
  sortReminders,
} from "../plan-management/components/reminder-mapping";

/**
 * LF360-1868 — the project's incomplete-task reminder, as the reminders editor
 * wants it and back again.
 *
 * The editor was built for a task (LF360-1830) and stores reminders the same way
 * these settings do: the first one in the row's own columns, the rest in
 * `extraReminders`. Only two things differ, and they are all this module is for.
 *
 * The first: a task has a `remind` flag, these settings have `enabled`. They are
 * not the same switch — `enabled` turns the whole feature off for the project,
 * while the reminder times stay as they were, ready for when it is turned back on.
 * So the list is always read as present.
 *
 * The second: an empty list. A task may have no reminder at all; this reminder is
 * either off or has a time, so the editor keeps at least one row and the payload
 * simply leaves the time out when there is somehow none — an absent time means
 * "unchanged" to the backend, which is the safe reading.
 */

export const remindersFromSettings = (settings = {}) =>
  remindersFromActivity({ ...settings, remind: true });

export const settingsFromReminders = (reminders = []) => {
  // The list may hold both kinds, so the type of the one that ends up in the row's
  // own columns has to be read off that reminder — assuming absolute would turn a
  // relative first reminder into midnight, quietly.
  const ordered = sortReminders(reminders.filter(Boolean));
  const [first] = ordered;
  const baseType = first?.notificationType === "relative" ? "relative" : "absolute";
  const payload = remindersToPayload(ordered, baseType);

  const next = {
    notificationType: baseType,
    extraReminders: payload.extraReminders,
  };

  if (baseType === "relative") {
    next.triggerOffsetMinutes = payload.triggerOffsetMinutes;
    // LF360-1896: the first reminder's field travels with it into the settings'
    // own column. The screen used to ask for it in a form row of its own and send
    // it separately; now every reminder names its own inside the picker, and
    // dropping it here would store an offset counting from nothing.
    next.triggerSubjectProfileFieldId = payload.triggerSubjectProfileFieldId;
    return next;
  }

  // An absolute first reminder leaves nothing to count from. Said explicitly so a
  // list that used to start with an offset does not keep a stale field.
  next.triggerSubjectProfileFieldId = null;

  if (Number.isInteger(payload.remindHour)) {
    next.remindHour = payload.remindHour;
    next.remindMinute = Number.isInteger(payload.remindMinute)
      ? payload.remindMinute
      : 0;
  }

  return next;
};
